/**
 * The pure half of the placeholder sync: which programs get a year-3008 placeholder, its dates and
 * base fields, what to create / update / delete, and which offers the count fan-out touches.
 *
 * No Firestore, no clock of its own (every rule takes `now`), and no imports beyond the two pure
 * discovery modules. That is what lets ONE implementation serve both callers:
 *   - the admin client, through `ensurePlaceholderOffersForLocation` (`placeholderOfferService.ts`);
 *   - the camp-ingest publish, through `scripts/camp-data/_operator/publishLocationLikeAdminLib.cjs`,
 *     which loads this file with `scripts/camp-data/_shared/tsModuleLoader.cjs`. A `.cjs` cannot
 *     `require` a `.ts` file, and the client never imports a `.cjs`, so the node side transpiles this
 *     source rather than keeping a port that could drift.
 */
import {
  collectVariationKeysForPlaceholders,
  isOfferRelevantForPlaceholderDiscovery
} from "./placeholderOfferUtils";
import {
  collectPlaceholderDiscoveryExceptionKeys,
  type PlaceholderDiscoveryExceptionKeys
} from "./placeholderDiscoveryException";

export const PLACEHOLDER_DISPLAY_TITLE = "__placeholder__";

/** The offer fields the sync reads. */
export interface PlaceholderSyncOffer {
  id: string;
  isPlaceholder?: boolean | null;
  variationKey?: string | null;
  endDate?: unknown;
  myOfferIncrementKey?: string | null;
}

/** The location fields the sync reads. */
export interface PlaceholderSyncLocation {
  visibility?: unknown;
  placeholderOffersCount?: unknown;
  variations?: ReadonlyArray<{ key?: unknown; regularOfferIncrementKey?: unknown; placeholderDiscoveryUntil?: unknown }> | null;
}

/**
 * Start Aug 1, 3008 11:00 UTC. End = start + (numDays − 1) days when the increment key resolved to a
 * positive day count, else Aug 5, 3008 11:00 UTC. 11:00 keeps the calendar day stable in US zones.
 */
export function placeholderDates(numDays: number | null | undefined): { startDate: Date; endDate: Date } {
  // JS Date month is 0-based; 7 => August
  const startDate = new Date(Date.UTC(3008, 7, 1));
  startDate.setUTCHours(11, 0, 0, 0);
  if (numDays && numDays > 0) {
    const endDate = new Date(startDate.getTime());
    endDate.setUTCDate(endDate.getUTCDate() + (numDays - 1));
    endDate.setUTCHours(11, 0, 0, 0);
    return { startDate, endDate };
  }
  const endDate = new Date(Date.UTC(3008, 7, 5));
  endDate.setUTCHours(11, 0, 0, 0);
  return { startDate, endDate };
}

/**
 * The increment key that sets a placeholder's length: the program's `regularOfferIncrementKey`, else
 * the `myOfferIncrementKey` of a discovery-relevant real session of that program, else null.
 */
export function resolvePlaceholderIncrementKey(
  variationKey: string,
  variationByKey: ReadonlyMap<string, { regularOfferIncrementKey?: unknown }>,
  nonPlaceholders: readonly PlaceholderSyncOffer[],
  now: Date
): string | null {
  const v = variationByKey.get(variationKey);
  if (v?.regularOfferIncrementKey) {
    return v.regularOfferIncrementKey as string;
  }
  for (const o of nonPlaceholders) {
    if (o.variationKey !== variationKey) continue;
    if (!isOfferRelevantForPlaceholderDiscovery(o as any, now)) continue;
    if (o.myOfferIncrementKey) return o.myOfferIncrementKey;
  }
  return null;
}

/** The fields the sync owns on a placeholder offer; the offer-prep seam derives everything else. */
export function placeholderBaseData(
  variationKey: string,
  incrementKey: string | null,
  dates: { startDate: Date; endDate: Date }
) {
  return {
    variationKey,
    startDate: dates.startDate,
    endDate: dates.endDate,
    isPlaceholder: true as const,
    displayTitle: PLACEHOLDER_DISPLAY_TITLE,
    myOfferIncrementKey: incrementKey,
    availabilityStatus: "av_unknown",
    signupStatus: "su_unknown"
  };
}

export interface PlaceholderUpsert {
  variationKey: string;
  incrementKey: string | null;
  /** The placeholder this key keeps, or null to create one. */
  existingId: string | null;
}

export interface PlaceholderSyncPlan {
  /** The discovery set, sorted: one placeholder per key. */
  desiredKeys: string[];
  /** Variations whose `placeholderDiscoveryUntil` grants nothing (the caller logs them). */
  invalidExceptions: PlaceholderDiscoveryExceptionKeys["invalid"];
  /** In `desiredKeys` order. */
  upserts: PlaceholderUpsert[];
  /** Placeholder offer ids whose key left the discovery set. */
  deletes: string[];
  newCount: number;
  currentCount: number;
  countChanged: boolean;
}

/**
 * Plan one location's sync. Returns null for a location that is not `v_published`: placeholders are
 * managed for published locations only.
 *
 * Two existing behaviours are kept on purpose, because the admin client has always done them:
 *   - when two placeholders share a key, the LAST one read is the one kept and updated; the other is
 *     neither updated nor deleted;
 *   - a placeholder with no `variationKey` is left alone.
 */
export function planPlaceholderSync(
  location: PlaceholderSyncLocation,
  offers: readonly PlaceholderSyncOffer[],
  now: Date
): PlaceholderSyncPlan | null {
  if (location.visibility !== "v_published") return null;

  const programs = (location.variations || []) as NonNullable<PlaceholderSyncLocation["variations"]>;
  // Every defined program key (no "__" name filter — offers can still use those keys).
  const variationByKey = new Map<string, { regularOfferIncrementKey?: unknown }>();
  for (const v of programs) {
    if (v?.key) variationByKey.set(v.key as string, v);
  }

  const placeholders = offers.filter(o => o.isPlaceholder === true);
  const nonPlaceholders = offers.filter(o => !o.isPlaceholder);
  const desiredKeys = collectVariationKeysForPlaceholders(nonPlaceholders as any, programs as any, now);
  const { invalid } = collectPlaceholderDiscoveryExceptionKeys(programs as any, now);

  const placeholdersByVariation = new Map<string, PlaceholderSyncOffer>();
  placeholders.forEach(p => {
    if (p.variationKey) placeholdersByVariation.set(p.variationKey, p);
  });

  const sortedDesiredKeys = [...desiredKeys].sort();
  const upserts: PlaceholderUpsert[] = sortedDesiredKeys.map(variationKey => ({
    variationKey,
    incrementKey: resolvePlaceholderIncrementKey(variationKey, variationByKey, nonPlaceholders, now),
    existingId: placeholdersByVariation.get(variationKey)?.id ?? null
  }));

  const deletes = placeholders
    .filter(p => p.variationKey && !desiredKeys.has(p.variationKey))
    .map(p => p.id);

  const newCount = desiredKeys.size;
  const currentCount = typeof location.placeholderOffersCount === "number" ? location.placeholderOffersCount : 0;

  return {
    desiredKeys: sortedDesiredKeys,
    invalidExceptions: invalid,
    upserts,
    deletes,
    newCount,
    currentCount,
    countChanged: newCount !== currentCount
  };
}

/** The warning the sync logs for one invalid `placeholderDiscoveryUntil`. */
export function describeInvalidPlaceholderException(
  locationId: string,
  bad: PlaceholderDiscoveryExceptionKeys["invalid"][number]
): string {
  return (
    `[placeholders] ${locationId}: variation "${bad.key}" has placeholderDiscoveryUntil=${JSON.stringify(bad.raw)}, ` +
    (bad.problem === "malformed"
      ? "not a YYYY-MM-DD day"
      : "later than any expiry the Sept-1-after-next-summer rule can produce today") +
    " — ignored; the 13-month rule alone decides this program's placeholder."
  );
}

/**
 * The offers the count fan-out updates: every offer read before the sync, minus the placeholders it
 * deleted (an update on a missing doc fails the whole batch), plus the ones it created (they were
 * stamped with the location's previous count).
 */
export function placeholderCountFanOutIds(
  offerIdsReadBeforeSync: readonly string[],
  deletedIds: ReadonlySet<string>,
  createdIds: readonly string[]
): string[] {
  return [...offerIdsReadBeforeSync.filter(id => !deletedIds.has(id)), ...createdIds];
}
